<template>
  <div class="holographic-product-center">
    <!-- 顶部导航组件 -->
    <TheHeader />
    
    <!-- 全息投影背景系统 -->
    <div class="holographic-space">
      <!-- 三维网格背景 -->
      <div class="dimensional-grid">
        <div class="grid-line" v-for="line in 20" :key="'line-' + line" :style="getGridLineStyle(line)"></div>
      </div>
      
      <!-- 量子粒子场 -->
      <div class="quantum-field">
        <div class="quantum-dot" v-for="dot in 50" :key="'dot-' + dot" :style="getQuantumDotStyle(dot)"></div>
      </div>
      
      <!-- 数据流连接线 -->
      <div class="data-streams">
        <svg class="stream-svg" width="100%" height="100%">
          <path 
            v-for="(stream, index) in dataStreams" 
            :key="'stream-' + index"
            :d="stream.path" 
            class="stream-path"
            :style="{ animationDelay: stream.delay + 's' }"
          />
        </svg>
      </div>
    </div>

    <!-- 全息投影页面头部 -->
    <div class="holographic-header">
      <div class="header-projection">
        <div class="projection-content">
          <!-- 主标题全息投影 -->
          <div class="title-hologram">
            <div class="scanner-line"></div>
            <h1 class="holographic-title">
              <span 
                v-for="(char, index) in '产品中心'.split('')" 
                :key="'char-' + index"
                class="holo-char"
                :style="{ animationDelay: (index * 0.2) + 's' }"
              >{{ char }}</span>
            </h1>
          </div>
          
          <!-- 全息面包屑导航 -->
          <div class="holographic-breadcrumb">
            <div class="nav-particle"></div>
            <router-link to="/" class="holo-link">
              <span class="link-text">主控制台</span>
              <div class="link-energy"></div>
            </router-link>
            <div class="nav-separator">/</div>
            <span class="current-location">产品中心</span>
          </div>
        </div>
        
        <!-- 全息投影边框 -->
        <div class="projection-borders">
          <div class="border-line border-top"></div>
          <div class="border-line border-right"></div>
          <div class="border-line border-bottom"></div>
          <div class="border-line border-left"></div>
        </div>
      </div>
    </div>

    <!-- 磁性吸附控制面板 -->
    <div class="magnetic-control-panel">
      <div class="control-matrix">
        <!-- 磁性分类选择器 -->
        <div class="magnetic-category-selector">
          <div class="selector-header">
            <div class="header-pulse"></div>
            <span class="selector-title">分类磁场</span>
            <div class="magnetic-indicator"></div>
          </div>
          
          <div class="magnetic-categories">
            <div
              class="magnetic-tag"
              :class="{ active: !activeCategoryId, 'magnetic-attracted': !activeCategoryId }"
              @click="handleCategoryClick(null)"
              @mouseenter="activateMagneticField"
              @mouseleave="deactivateMagneticField"
            >
              <div class="tag-core">
                <span class="tag-text">全部产品</span>
                <div class="magnetic-field"></div>
              </div>
              <div class="attraction-rings">
                <div class="ring" v-for="ring in 3" :key="'ring-' + ring"></div>
              </div>
            </div>
            
            <div
              v-for="category in categories"
              :key="category.id"
              class="magnetic-tag"
              :class="{ active: activeCategoryId === category.id, 'magnetic-attracted': activeCategoryId === category.id }"
              @click="handleCategoryClick(category.id)"
              @mouseenter="activateMagneticField"
              @mouseleave="deactivateMagneticField"
            >
              <div class="tag-core">
                <span class="tag-text">{{ category.name }}</span>
                <div class="magnetic-field"></div>
              </div>
              <div class="attraction-rings">
                <div class="ring" v-for="ring in 3" :key="'ring-' + ring"></div>
              </div>
            </div>
          </div>
          
          <!-- 分类加载状态 -->
          <div v-if="categoryLoading" class="magnetic-loading">
            <div class="loading-magnet">
              <div class="magnet-core"></div>
              <div class="magnet-field" v-for="field in 4" :key="'field-' + field"></div>
            </div>
            <span class="loading-text">磁场校准中...</span>
          </div>
        </div>

        <!-- 量子搜索引擎 -->
        <div class="quantum-search-engine">
          <div class="search-header">
            <div class="quantum-scanner"></div>
            <span class="search-title">搜索</span>
          </div>
          
          <div class="search-portal">
            <div class="portal-frame">
              <input
                type="text"
                v-model="searchKeyword"
                placeholder="输入药名..."
                class="quantum-input"
                @keyup.enter="handleSearch"
                @focus="activateQuantumField"
                @blur="deactivateQuantumField"
              />
              <div class="input-energy-field"></div>
              <div class="quantum-particles">
                <div class="particle" v-for="particle in 8" :key="'particle-' + particle"></div>
              </div>
            </div>
            
            <button @click="handleSearch" class="quantum-search-btn">
              <div class="btn-core">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </div>
              <div class="btn-quantum-trail"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 3D时空隧道产品展示区域 -->
    <div class="spacetime-product-matrix">
      <!-- 时空隧道背景 -->
      <div class="spacetime-tunnel">
        <div class="tunnel-ring" v-for="ring in 10" :key="'tunnel-' + ring" :style="getTunnelRingStyle(ring)"></div>
      </div>

      <!-- 加载状态 - 量子重构 -->
      <div v-if="loading" class="quantum-reconstruction">
        <div class="reconstruction-core">
          <div class="quantum-spinner">
            <div class="spinner-ring" v-for="ring in 3" :key="'spinner-' + ring"></div>
          </div>
          <div class="reconstruction-particles">
            <div class="particle" v-for="particle in 12" :key="'particle-' + particle" :style="getParticleStyle(particle)"></div>
          </div>
        </div>
        <div class="loading-hologram">正在重构产品数据...</div>
      </div>
      
      <!-- 错误状态 - 系统异常 -->
      <div v-else-if="error" class="system-anomaly">
        <div class="anomaly-field">
          <div class="anomaly-distortion">
            <el-icon class="anomaly-icon"><WarningFilled /></el-icon>
          </div>
          <div class="error-waves">
            <div class="wave" v-for="wave in 4" :key="'wave-' + wave"></div>
          </div>
        </div>
        <div class="anomaly-message">{{ error }}</div>
        <button class="quantum-retry" @click="loadData">
          <span class="retry-text">重新连接</span>
          <div class="retry-energy"></div>
        </button>
      </div>
      
      <!-- 虚空状态 - 无数据 -->
      <div v-else-if="productList.length === 0" class="void-dimension">
        <div class="void-field">
          <div class="void-center">
            <div class="void-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div class="void-ripples">
              <div class="ripple" v-for="ripple in 3" :key="'ripple-' + ripple"></div>
            </div>
          </div>
        </div>
        <div class="void-message">此维度暂无产品存在</div>
      </div>
      
      <!-- 重力悬浮产品矩阵 -->
      <div v-else class="gravity-product-matrix">
        <div
          v-for="(product, index) in productList"
          :key="product.id"
          class="floating-product-cell"
          :style="getProductCellStyle(index)"
          @click="showPaymentDialog(product)"
          @mouseenter="activateProductField(index)"
          @mouseleave="deactivateProductField(index)"
        >
          <!-- 产品能量场 -->
          <div class="product-energy-field">
            <div class="field-rings">
              <div class="energy-ring" v-for="ring in 3" :key="'ring-' + ring"></div>
            </div>
          </div>

          <!-- 产品全息投影容器 -->
          <div class="holographic-product-container">
            <!-- 全息图像投影 -->
            <div class="holographic-image">
              <div class="image-projection">
                <img
                  :src="getFullImageUrl(product.image) || placeholderImages.med1"
                  :alt="product.name"
                  class="product-holo-image"
                  @error="handleImageError"
                />
                <div class="projection-overlay">
                  <div class="scan-lines">
                    <div class="scan-line" v-for="line in 5" :key="'scan-' + line"></div>
                  </div>
                  <div class="hologram-distortion"></div>
                </div>
              </div>
              
              <!-- 已购买标签 -->
              <div v-if="isProductPermitted(product.id)" class="purchased-badge">
                <span>✓ 已购买</span>
              </div>
              
              <!-- 分类标识投影 -->
              <div class="category-projection">
                <div class="category-core">{{ getCategoryName(product.category_id) }}</div>
                <div class="category-glow"></div>
              </div>
            </div>

            <!-- 信息数据流投影 -->
            <div class="data-stream-projection">
              <div class="product-name-hologram">
                <h3 class="holo-name">{{ product.name }}</h3>
                <div class="name-scanner"></div>
              </div>
              
              <div class="product-data-matrix">
                <div class="data-row" v-if="product.manufacturer">
                  <span class="data-label">制造商</span>
                  <span class="data-value">{{ getShortManufacturer(product.manufacturer) }}</span>
                  <div class="data-flow-line"></div>
                </div>
                <div class="data-row" v-if="product.approval_number">
                  <span class="data-label">批准号</span>
                  <span class="data-value">{{ product.approval_number }}</span>
                  <div class="data-flow-line"></div>
                </div>
                <div class="data-row" v-if="product.description || product.indications">
                  <span class="data-label">适应症</span>
                  <span class="data-value">{{ getShortDescription(product.description) || (product.indications ? getShortIndications(product.indications) : '暂无描述') }}</span>
                  <div class="data-flow-line"></div>
                </div>
                <!-- <div class="data-row price-row">
                  <span class="data-label">查看详情</span>
                  <span class="data-value price-highlight">¥{{ product.price }}</span>
                  <div class="data-flow-line"></div>
                </div> -->
              </div>

              <!-- 量子访问按钮 -->
              <!-- <div class="quantum-access">
                <div class="access-btn" @click.stop="showPaymentDialog(product)">
                  <span class="btn-text">付费查看</span>
                  <div class="btn-energy-trail"></div>
                </div>
              </div> -->
            </div>
          </div>

          <!-- 产品重力扭曲效果 -->
          <div class="gravity-distortion">
            <div class="distortion-field" v-for="field in 2" :key="'field-' + field"></div>
          </div>
        </div>
      </div>
      
      <!-- 量子分页导航 -->
      <div v-if="pagination.total > 0" class="quantum-pagination">
        <div class="pagination-field">
          <div class="field-energy"></div>
          <el-pagination
            background
            layout="prev, pager, next"
            :total="pagination.total"
            :page-size="pagination.pageSize"
            :current-page="pagination.currentPage"
            @current-change="handleCurrentChange"
          />
          <div class="pagination-glow"></div>
        </div>
      </div>
    </div>
    
    <!-- 支付二维码弹窗 -->
    <div v-if="paymentDialogVisible" class="payment-dialog-overlay" @click="closePaymentDialog">
      <div class="payment-dialog" @click.stop>
        <div class="payment-header">
          <h3>付费查看详情</h3>
          <button class="close-btn" @click="closePaymentDialog">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div class="payment-content">
          <div class="product-info">
            <h4>{{ currentProduct.name }}</h4>
            <p class="price">¥ {{ currentPrice }}</p>
          </div>
          <div class="qr-code-container">
            <img :src="require('@/assets/zhifu.png')" alt="支付二维码" class="qr-code" />
            <p class="qr-tip">扫码支付后即可查看完整详情</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部信息组件 -->
    <TheFooter />
  </div>
</template>

<script>
// 导入Element Plus组件
import { ElPagination, ElIcon } from 'element-plus'
import { WarningFilled } from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'

// 导入占位图片
import { placeholderImages } from '@/assets/placeholder.js'

// 导入自定义组件
import TheHeader from '@/components/TheHeader.vue'
import TheFooter from '@/components/TheFooter.vue'

// 导入API
import { getMedicines, getMedicinesByCategory, getMedicineCategories, searchMedicineAPI, BASE_URL, getPermittedMedicines } from '@/api/config'
// 导入用户状态管理
import { getToken, isLoggedIn } from '@/utils/userStore'

export default {
  name: 'ProductCenter',
  components: {
    ElPagination,
    ElIcon,
    WarningFilled,
    TheHeader,
    TheFooter
  },
  data() {
    return {
      // 产品相关数据
      productList: [],
      loading: false,
      error: null,
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      
      // 分类相关
      categories: [],
      categoryLoading: false,
      categoryError: null,
      activeCategoryId: null,
      
      // 搜索相关
      searchKeyword: '',
      
      // 占位图片
      placeholderImages,

      // 全息投影数据
      dataStreams: [],
      
      // 支付弹窗相关数据
      paymentDialogVisible: false,
      currentProduct: {},
      currentPrice: 0,
      
      // 权限药品列表
      permittedMedicineIds: [],
    }
  },
  methods: {
    
    // 加载数据
    async loadData() {
      this.loading = true;
      this.error = null;
      
      try {
        let response;
        const { currentPage, pageSize } = this.pagination;
        
        if (this.searchKeyword) {
          // 搜索模式
          response = await searchMedicineAPI(this.searchKeyword, currentPage, pageSize);
        } else if (this.activeCategoryId) {
          // 分类筛选模式
          response = await getMedicinesByCategory(this.activeCategoryId, currentPage, pageSize);
        } else {
          // 默认模式 - 所有产品
          response = await getMedicines({
            page: currentPage,
            pagesize: pageSize
          });
        }
        
        if (response.code === 1) {
          // 使用API返回的价格
          this.productList = response.data.data || [];
          this.pagination.total = response.data.total || 0;
        } else {
          this.error = response.msg || '获取产品数据失败';
        }
      } catch (error) {
        console.error('获取产品数据错误:', error);
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    
    // 加载分类数据
    async loadCategories() {
      this.categoryLoading = true;
      this.categoryError = null;
      
      try {
        const response = await getMedicineCategories();
        
        if (response.code === 1) {
          this.categories = response.data.sort((a, b) => b.weigh - a.weigh);
        } else {
          this.categoryError = response.msg || '获取分类数据失败';
          console.error('获取分类失败:', this.categoryError);
        }
      } catch (error) {
        console.error('获取分类数据错误:', error);
        this.categoryError = '网络错误，请稍后重试';
      } finally {
        this.categoryLoading = false;
      }
    },
    
    // 处理分类点击
    handleCategoryClick(categoryId) {
      if (this.activeCategoryId === categoryId) return;
      
      // 通过URL查询参数来设置分类筛选
      if (categoryId) {
        this.$router.push({
          name: 'Products',
          query: { category: categoryId }
        });
      } else {
        this.$router.push({ name: 'Products' });
      }
      
      // 分类和页码的更新会通过watch处理
    },
    
    // 处理搜索
    handleSearch() {
      if (this.searchKeyword.trim() === '') {
        // 如果搜索关键词为空，重置回默认状态
        if (this.activeCategoryId) {
          // 如果有选中的分类，则保持分类筛选
          this.loadData();
        } else {
          // 否则加载全部数据
          this.loadData();
        }
        return;
      }
      
      // 执行搜索查询
      this.pagination.currentPage = 1;
      this.loadData();
    },
    
    // 处理分页变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadData();
    },
    
    // 提取短厂商名称
    getShortManufacturer(manufacturer) {
      if (!manufacturer) return '暂无';
      return manufacturer.length > 10 ? manufacturer.substring(0, 10) + '...' : manufacturer;
    },
    
    // 从描述HTML中提取纯文本短描述
    getShortDescription(description) {
      if (!description) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = description;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    },
    
    // 从适应症HTML中提取纯文本短描述
    getShortIndications(indications) {
      if (!indications) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = indications;
      const text = tempDiv.textContent || tempDiv.innerText || '';
      return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    },
    
    // 获取完整图片URL
    getFullImageUrl(path) {
      if (!path) return null;
      
      // 如果已经是完整URL则直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 否则拼接基础URL
      return `${BASE_URL}${path}`;
    },
    
    // 根据分类ID获取分类名称
    getCategoryName(categoryId) {
      if (!categoryId) return '医疗健康';
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.name : '医疗健康';
    },

    // 获取网格线样式
    getGridLineStyle(index) {
      const isVertical = index % 2 === 0;
      return {
        position: 'absolute',
        background: 'linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent)',
        animationDelay: index * 0.2 + 's',
        ...(isVertical ? {
          left: Math.random() * 100 + '%',
          top: '0',
          bottom: '0',
          width: '1px',
          animation: 'gridLineVertical 3s ease-in-out infinite'
        } : {
          top: Math.random() * 100 + '%',
          left: '0',
          right: '0',
          height: '1px',
          animation: 'gridLineHorizontal 3s ease-in-out infinite'
        })
      }
    },

    // 获取量子点样式
    getQuantumDotStyle(index) {
      // 使用index创建更有序的随机效果
      const baseHue = 200 + (index % 10) * 6;
      const baseDelay = (index % 20) * 0.25;
      return {
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animationDelay: baseDelay + 's',
        '--dot-color': `hsl(${baseHue}, 70%, 60%)`,
        '--dot-size': Math.random() * 4 + 2 + 'px'
      }
    },

    // 获取隧道环样式
    getTunnelRingStyle(index) {
      const size = 200 + index * 150;
      const opacity = 1 - (index * 0.08);
      return {
        width: size + 'px',
        height: size + 'px',
        opacity: opacity,
        animationDelay: index * 0.5 + 's',
        border: `2px solid rgba(0, 212, 255, ${opacity * 0.3})`
      }
    },

    // 获取粒子样式
    getParticleStyle(index) {
      const angle = (index / 12) * 360;
      return {
        transform: `rotate(${angle}deg) translateX(${60 + Math.random() * 40}px)`,
        animationDelay: index * 0.1 + 's',
        '--particle-hue': Math.random() * 360
      }
    },

    // 获取产品单元格样式
    getProductCellStyle(index) {
      const row = Math.floor(index / 3);
      const col = index % 3;
      return {
        animationDelay: (row * 0.2 + col * 0.1) + 's',
        '--float-amplitude': Math.random() * 20 + 10 + 'px',
        '--rotation-axis': Math.random() * 360 + 'deg'
      }
    },

    // 激活产品磁场
    activateProductField(index) {
      const element = document.querySelector(`.floating-product-cell:nth-child(${index + 1})`);
      if (element) {
        element.classList.add('field-activated');
      }
    },

    // 停用产品磁场
    deactivateProductField(index) {
      const element = document.querySelector(`.floating-product-cell:nth-child(${index + 1})`);
      if (element) {
        element.classList.remove('field-activated');
      }
    },

    // 激活磁性场
    activateMagneticField(event) {
      if (event.target.closest) {
        const element = event.target.closest('.magnetic-tag');
        if (element) {
          element.classList.add('magnetic-hover');
        }
      }
    },

    // 停用磁性场
    deactivateMagneticField(event) {
      if (event.target.closest) {
        const element = event.target.closest('.magnetic-tag');
        if (element) {
          element.classList.remove('magnetic-hover');
        }
      }
    },

    // 激活量子场
    activateQuantumField() {
      const element = document.querySelector('.quantum-search-engine');
      if (element) {
        element.classList.add('quantum-active');
      }
    },

    // 停用量子场
    deactivateQuantumField() {
      const element = document.querySelector('.quantum-search-engine');
      if (element) {
        element.classList.remove('quantum-active');
      }
    },

    // 检查药品是否有权限
    isProductPermitted(productId) {
      return this.permittedMedicineIds.includes(productId);
    },
    
    // 加载权限药品列表
    async loadPermittedMedicines() {
      if (!isLoggedIn()) {
        this.permittedMedicineIds = [];
        return;
      }
      
      try {
        const token = getToken();
        const response = await getPermittedMedicines(token, 1, 100);
        
        if (response.code === 1 && response.data && response.data.data) {
          this.permittedMedicineIds = response.data.data.map(item => item.id);
        }
      } catch (error) {
        console.error('加载权限药品列表失败:', error);
      }
    },

    // 显示支付弹窗或跳转详情页
    async showPaymentDialog(product) {
      // 检查用户是否已登录
      if (!isLoggedIn()) {
        // 未登录，显示支付弹窗
        this.currentProduct = product;
        this.currentPrice = product.price;
        this.paymentDialogVisible = true;
        return;
      }
      
      // 已登录，检查是否有权限
      try {
        const token = getToken();
        const response = await getPermittedMedicines(token, 1, 100);
        
        if (response.code === 1 && response.data && response.data.data) {
          // 检查该药品是否在有权限列表中
          const permittedIds = response.data.data.map(item => item.id);
          
          if (permittedIds.includes(product.id)) {
            // 有权限，跳转到详情页
            this.$router.push({ name: 'ProductDetail', params: { id: product.id } });
          } else {
            // 没有权限，显示支付弹窗
            this.currentProduct = product;
            this.currentPrice = product.price;
            this.paymentDialogVisible = true;
          }
        } else {
          // API调用失败，显示支付弹窗
          this.currentProduct = product;
          this.currentPrice = product.price;
          this.paymentDialogVisible = true;
        }
      } catch (error) {
        console.error('检查权限失败:', error);
        // 错误时显示支付弹窗
        this.currentProduct = product;
        this.currentPrice = product.price;
        this.paymentDialogVisible = true;
      }
    },

    // 关闭支付弹窗
    closePaymentDialog() {
      this.paymentDialogVisible = false;
      this.currentProduct = {};
      this.currentPrice = 0;
    },

    // 生成数据流
    generateDataStreams() {
      const streams = [];
      for (let i = 0; i < 8; i++) {
        const startX = Math.random() * 100;
        const startY = Math.random() * 100;
        const endX = Math.random() * 100;
        const endY = Math.random() * 100;
        
        streams.push({
          path: `M${startX},${startY} Q${(startX + endX) / 2 + Math.random() * 50 - 25},${(startY + endY) / 2 + Math.random() * 50 - 25} ${endX},${endY}`,
          delay: Math.random() * 3
        });
      }
      this.dataStreams = streams;
    }
  },
  created() {
    // 检查URL参数是否包含category
    const categoryParam = this.$route.query.category;
    if (categoryParam) {
      this.activeCategoryId = parseInt(categoryParam, 10);
    }
    
    // 生成全息投影数据流
    this.generateDataStreams();
    
    // 加载权限药品列表
    this.loadPermittedMedicines();
    
    // 加载分类数据
    this.loadCategories();
    // 加载产品数据
    this.loadData();
  },
  watch: {
    // 监听路由变化，更新分类筛选
    '$route.query.category': function(newCategoryId) {
      if (newCategoryId) {
        this.activeCategoryId = parseInt(newCategoryId, 10);
      } else {
        this.activeCategoryId = null;
      }
      
      // 重置页码并重新加载数据
      this.pagination.currentPage = 1;
      this.loadData();
    }
  }
}
</script>

<style scoped>
/* 全息投影产品中心基础样式 */
.holographic-product-center {
  font-family: 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  background: radial-gradient(ellipse at center, #0a0a1a 0%, #000010 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.9);
}

/* 全息投影背景系统 */
.holographic-space {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
}

/* 三维网格背景 */
.dimensional-grid {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.grid-line {
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.2), transparent);
  box-shadow: 0 0 10px rgba(0, 212, 255, 0.1);
}

/* 量子粒子场 */
.quantum-field {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

.quantum-dot {
  position: absolute;
  width: var(--dot-size, 3px);
  height: var(--dot-size, 3px);
  background: var(--dot-color, #00d4ff);
  border-radius: 50%;
  box-shadow: 0 0 10px var(--dot-color, #00d4ff);
  animation: quantumFloat 4s ease-in-out infinite;
}

/* 数据流连接线 */
.data-streams {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

.stream-svg {
  width: 100%;
  height: 100%;
}

.stream-path {
  fill: none;
  stroke: rgba(0, 212, 255, 0.3);
  stroke-width: 2;
  stroke-dasharray: 10, 5;
  animation: streamFlow 3s linear infinite;
}

/* 全息投影页面头部 */
.holographic-header {
  position: relative;
  margin-top: 80px;
  padding: 60px 0;
  z-index: 10;
  perspective: 1000px;
}

.header-projection {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  background: rgba(0, 212, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 0 50px rgba(0, 212, 255, 0.2);
}

.projection-content {
  position: relative;
  z-index: 2;
}

/* 主标题全息投影 */
.title-hologram {
  text-align: center;
  position: relative;
  margin-bottom: 30px;
}

.scanner-line {
  position: absolute;
  top: -10px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  animation: scannerMove 2s ease-in-out infinite;
}

.holographic-title {
  font-size: 4rem;
  font-weight: 900;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  position: relative;
}

.holo-char {
  display: inline-block;
  color: #ffffff;
  text-shadow: 
    0 0 10px rgba(0, 212, 255, 0.8),
    0 0 20px rgba(0, 212, 255, 0.6),
    0 0 30px rgba(0, 212, 255, 0.4);
  animation: charFloat 3s ease-in-out infinite;
  transform-origin: center;
}

.subtitle-glow {
  font-size: 1.2rem;
  color: rgba(0, 212, 255, 0.9);
  margin-top: 10px;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.5);
  letter-spacing: 0.05em;
}

/* 全息面包屑导航 */
.holographic-breadcrumb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  font-size: 1rem;
}

.nav-particle {
  width: 4px;
  height: 4px;
  background: #00d4ff;
  border-radius: 50%;
  box-shadow: 0 0 10px #00d4ff;
  animation: pulse 2s ease-in-out infinite;
}

.holo-link {
  position: relative;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  padding: 5px 10px;
}

.link-text {
  position: relative;
  z-index: 2;
}

.link-energy {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(0, 212, 255, 0.2), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.holo-link:hover .link-energy {
  opacity: 1;
}

.nav-separator {
  color: rgba(0, 212, 255, 0.6);
  font-weight: bold;
}

.current-location {
  color: #00d4ff;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

/* 全息投影边框 */
.projection-borders {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.border-line {
  position: absolute;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.6), transparent);
}

.border-top {
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  animation: borderScan 3s ease-in-out infinite;
}

.border-right {
  top: 0;
  right: 0;
  bottom: 0;
  width: 2px;
  animation: borderScan 3s ease-in-out infinite 0.75s;
}

.border-bottom {
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  animation: borderScan 3s ease-in-out infinite 1.5s;
}

.border-left {
  top: 0;
  left: 0;
  bottom: 0;
  width: 2px;
  animation: borderScan 3s ease-in-out infinite 2.25s;
}

/* 磁性吸附控制面板 */
.magnetic-control-panel {
  position: relative;
  z-index: 10;
  padding: 40px 0;
  max-width: 1400px;
  margin: 0 auto;
}

.control-matrix {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  padding: 0 20px;
}

/* 磁性分类选择器 */
.magnetic-category-selector {
  background: rgba(0, 212, 255, 0.05);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 30px;
  border: 2px solid rgba(0, 212, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.selector-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  position: relative;
}

.header-pulse {
  width: 8px;
  height: 8px;
  background: #00d4ff;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  box-shadow: 0 0 15px #00d4ff;
}

.selector-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
}

.magnetic-indicator {
  position: absolute;
  right: 0;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 212, 255, 0.4);
  border-radius: 50%;
  animation: rotate 3s linear infinite;
}

.magnetic-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.magnetic-tag {
  position: relative;
  cursor: pointer;
  transition: all 0.4s ease;
  transform-style: preserve-3d;
}

.tag-core {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(0, 212, 255, 0.3);
  border-radius: 25px;
  padding: 15px 25px;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
}

.tag-text {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  position: relative;
  z-index: 2;
  text-shadow: 0 0 5px rgba(0, 212, 255, 0.3);
}

.magnetic-field {
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: conic-gradient(from 0deg, transparent, rgba(0, 212, 255, 0.2), transparent);
  animation: rotate 4s linear infinite;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.magnetic-tag:hover .magnetic-field,
.magnetic-tag.active .magnetic-field {
  opacity: 1;
}

.magnetic-tag.active .tag-core {
  background: rgba(0, 212, 255, 0.2);
  border-color: rgba(0, 212, 255, 0.8);
  box-shadow: 0 0 25px rgba(0, 212, 255, 0.4);
  transform: translateY(-5px);
}

.attraction-rings {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.ring {
  position: absolute;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50%;
  opacity: 0;
  animation: magneticPulse 2s ease-in-out infinite;
}

.ring:nth-child(1) {
  width: 60px;
  height: 60px;
  margin: -30px 0 0 -30px;
}

.ring:nth-child(2) {
  width: 80px;
  height: 80px;
  margin: -40px 0 0 -40px;
  animation-delay: 0.3s;
}

.ring:nth-child(3) {
  width: 100px;
  height: 100px;
  margin: -50px 0 0 -50px;
  animation-delay: 0.6s;
}

.magnetic-tag:hover .ring,
.magnetic-tag.active .ring {
  opacity: 1;
}

/* 磁性加载状态 */
.magnetic-loading {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.8);
}

.loading-magnet {
  position: relative;
  width: 30px;
  height: 30px;
}

.magnet-core {
  width: 8px;
  height: 8px;
  background: #00d4ff;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px #00d4ff;
}

.magnet-field {
  position: absolute;
  border: 1px solid rgba(0, 212, 255, 0.4);
  border-radius: 50%;
  animation: magneticFieldExpand 1.5s ease-out infinite;
}

.magnet-field:nth-child(2) {
  width: 15px;
  height: 15px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.magnet-field:nth-child(3) {
  width: 20px;
  height: 20px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 0.2s;
}

.magnet-field:nth-child(4) {
  width: 25px;
  height: 25px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 0.4s;
}

.magnet-field:nth-child(5) {
  width: 30px;
  height: 30px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation-delay: 0.6s;
}

/* 量子搜索引擎 */
.quantum-search-engine {
  background: rgba(139, 92, 246, 0.05);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 30px;
  border: 2px solid rgba(139, 92, 246, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
}

.quantum-search-engine.quantum-active {
  border-color: rgba(139, 92, 246, 0.6);
  box-shadow: 0 0 30px rgba(139, 92, 246, 0.3);
}

.search-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
}

.quantum-scanner {
  width: 8px;
  height: 8px;
  background: #8b5cf6;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  box-shadow: 0 0 15px #8b5cf6;
}

.search-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 0 10px rgba(139, 92, 246, 0.5);
}

.search-portal {
  display: flex;
  gap: 15px;
  align-items: center;
}

.portal-frame {
  position: relative;
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(139, 92, 246, 0.3);
  border-radius: 25px;
  overflow: hidden;
  transition: all 0.4s ease;
}

.portal-frame:focus-within {
  border-color: rgba(139, 92, 246, 0.8);
  box-shadow: 0 0 25px rgba(139, 92, 246, 0.4);
}

.quantum-input {
  width: 100%;
  padding: 15px 25px;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 500;
}

.quantum-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.input-energy-field {
  position: absolute;
  top: -50%;
  left: -50%;
  right: -50%;
  bottom: -50%;
  background: conic-gradient(from 0deg, transparent, rgba(139, 92, 246, 0.2), transparent);
  animation: rotate 6s linear infinite;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.portal-frame:focus-within .input-energy-field {
  opacity: 1;
}

.quantum-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 2px;
  height: 2px;
  background: rgba(139, 92, 246, 0.6);
  border-radius: 50%;
  animation: particleFloat 3s ease-in-out infinite;
}

.particle:nth-child(1) { top: 20%; left: 10%; animation-delay: 0s; }
.particle:nth-child(2) { top: 30%; left: 20%; animation-delay: 0.5s; }
.particle:nth-child(3) { top: 50%; left: 30%; animation-delay: 1s; }
.particle:nth-child(4) { top: 70%; left: 40%; animation-delay: 1.5s; }
.particle:nth-child(5) { top: 80%; left: 60%; animation-delay: 2s; }
.particle:nth-child(6) { top: 40%; left: 70%; animation-delay: 2.5s; }
.particle:nth-child(7) { top: 60%; left: 80%; animation-delay: 3s; }
.particle:nth-child(8) { top: 10%; left: 90%; animation-delay: 3.5s; }

.quantum-search-btn {
  background: linear-gradient(45deg, rgba(139, 92, 246, 0.8), rgba(236, 72, 153, 0.8));
  border: none;
  border-radius: 20px;
  padding: 15px 20px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  color: white;
}

.quantum-search-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 0 25px rgba(139, 92, 246, 0.6);
}

.btn-core {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-quantum-trail {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.quantum-search-btn:hover .btn-quantum-trail {
  transform: translateX(100%);
}

/* 3D时空隧道产品展示区域 */
.spacetime-product-matrix {
  position: relative;
  z-index: 10;
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 20px;
  perspective: 2000px;
}

/* 时空隧道背景 */
.spacetime-tunnel {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: -1;
}

.tunnel-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid rgba(0, 212, 255, 0.2);
  animation: tunnelPulse 4s ease-in-out infinite;
}

/* 量子重构加载状态 */
.quantum-reconstruction {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;
}

.reconstruction-core {
  position: relative;
  margin-bottom: 30px;
}

.quantum-spinner {
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: quantumSpin 2s linear infinite;
}

.spinner-ring:nth-child(1) {
  border-top-color: #00d4ff;
  animation-delay: 0s;
}

.spinner-ring:nth-child(2) {
  border-right-color: #8b5cf6;
  animation-delay: 0.5s;
  width: 60px;
  height: 60px;
  top: 10px;
  left: 10px;
}

.spinner-ring:nth-child(3) {
  border-bottom-color: #ec4899;
  animation-delay: 1s;
  width: 40px;
  height: 40px;
  top: 20px;
  left: 20px;
}

.reconstruction-particles {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.reconstruction-particles .particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: hsl(var(--particle-hue, 200), 70%, 60%);
  border-radius: 50%;
  animation: reconstructionOrbit 3s linear infinite;
  box-shadow: 0 0 8px hsl(var(--particle-hue, 200), 70%, 60%);
}

.loading-hologram {
  font-size: 1.3rem;
  color: rgba(0, 212, 255, 0.9);
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.5);
  animation: textGlow 2s ease-in-out infinite;
}

.title-section h1 {
  font-size: 4.5rem;
  font-weight: 900;
  margin-bottom: 20px;
  text-shadow: 0 0 40px rgba(255, 255, 255, 0.3),
               0 8px 16px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.02em;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 0.9) 50%,
    rgba(255, 255, 255, 1) 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: textShine 3s ease-in-out infinite;
}

.title-section p {
  font-size: 1.6rem;
  opacity: 0.95;
  margin-bottom: 30px;
  font-weight: 400;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 1rem;
  opacity: 0.8;
}

.breadcrumb a {
  color: white;
  text-decoration: none;
  transition: opacity 0.3s ease;
}

.breadcrumb a:hover {
  opacity: 1;
}

.separator {
  opacity: 0.6;
}

.current {
  font-weight: 600;
}

/* 震撼筛选区域样式 */
.filter-section {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15),
              0 0 0 1px rgba(255, 255, 255, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  z-index: 10;
  margin-top: -50px;
  border-radius: 30px 30px 0 0;
  border: 2px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
}

.filter-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 25%,
    rgba(236, 72, 153, 0.02) 50%,
    rgba(251, 191, 36, 0.04) 75%,
    rgba(34, 197, 94, 0.02) 100%);
  pointer-events: none;
}

.filter-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 50px 20px;
  position: relative;
  z-index: 2;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2c3e50 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 25px;
  position: relative;
}

.section-title svg {
  color: #3b82f6;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

/* 现代化分类标签样式 */
.category-section {
  margin-bottom: 50px;
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.category-tag {
  position: relative;
  background: rgba(59, 130, 246, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(59, 130, 246, 0.2);
  border-radius: 30px;
  padding: 15px 30px;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  font-weight: 700;
  color: #3b82f6;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.1);
}

.category-tag::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.6s ease;
}

.category-tag:hover::before {
  left: 100%;
}

.category-tag:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.3);
  border-color: rgba(59, 130, 246, 0.4);
  background: rgba(59, 130, 246, 0.15);
}

.category-tag.active {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899);
  background-size: 200% 200%;
  animation: gradientText 3s ease-in-out infinite;
  color: white;
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 12px 35px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(58, 142, 230, 0.3);
}

.tag-indicator {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: linear-gradient(45deg, #3a8ee6, #42b983);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.category-tag.active .tag-indicator {
  width: 80%;
}

/* 现代化搜索区域样式 */
.search-section {
  margin-bottom: 30px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 35px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1),
              0 0 0 1px rgba(255, 255, 255, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.4);
  border: 2px solid rgba(59, 130, 246, 0.2);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  overflow: hidden;
}

.search-input-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 50%,
    rgba(236, 72, 153, 0.02) 100%);
  pointer-events: none;
}

.search-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 20px 60px rgba(59, 130, 246, 0.3);
  transform: translateY(-2px);
}

.search-input {
  flex: 1;
  padding: 18px 30px;
  border: none;
  outline: none;
  font-size: 1.1rem;
  background: transparent;
  border-radius: 35px;
  font-weight: 500;
  position: relative;
  z-index: 2;
}

.search-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.search-btn {
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  border: none;
  padding: 16px 20px;
  border-radius: 0 30px 30px 0;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-btn:hover {
  background: linear-gradient(135deg, #2980b9, #27ae60);
  transform: scale(1.05);
}

.category-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 0.9rem;
  margin-top: 15px;
}

.loading-icon-sm {
  animation: spin 1s linear infinite;
}

/* 震撼产品内容区域 */
.product-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 20px;
  background: transparent;
  position: relative;
  z-index: 2;
}

/* 现代化产品网格 */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 40px;
  margin-bottom: 60px;
  perspective: 1000px;
}

.product-item {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 30px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12),
              0 0 0 1px rgba(255, 255, 255, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(25px);
  -webkit-backdrop-filter: blur(25px);
  position: relative;
  transform-style: preserve-3d;
  animation: productCardFloat 8s ease-in-out infinite;
}

.product-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 25%,
    rgba(236, 72, 153, 0.02) 50%,
    rgba(251, 191, 36, 0.04) 75%,
    rgba(34, 197, 94, 0.02) 100%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.product-item::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(59, 130, 246, 0.01), transparent);
  animation: rotate 15s linear infinite;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.product-item:hover {
  transform: translateY(-20px) rotateX(5deg) rotateY(5deg) scale(1.02);
  box-shadow: 0 40px 100px rgba(59, 130, 246, 0.25);
}

.product-item:hover::before,
.product-item:hover::after {
  opacity: 1;
}

.product-image-container {
  height: 250px;
  position: relative;
  overflow: hidden;
  border-radius: 25px 25px 0 0;
}

.product-image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  filter: brightness(0.9) contrast(1.1) saturate(1.2);
}

.product-item:hover .product-image-container img {
  transform: scale(1.15) rotate(2deg);
  filter: brightness(1.1) contrast(1.2) saturate(1.4);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(58, 142, 230, 0.8) 0%,
    rgba(66, 185, 131, 0.8) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.product-item:hover .image-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
}

.quick-view-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.quick-view-btn:hover {
  transform: scale(1.1);
}

.product-badge {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #3a8ee6;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.product-content {
  padding: 30px;
  position: relative;
  z-index: 2;
}

.product-header {
  margin-bottom: 25px;
}

.product-title {
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2c3e50 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0;
  line-height: 1.3;
  margin-bottom: 10px;
}

.product-details {
  margin-bottom: 30px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 18px;
  padding: 15px;
  background: rgba(59, 130, 246, 0.06);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border-left: 4px solid #3b82f6;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.detail-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(59, 130, 246, 0.02) 0%,
    rgba(139, 92, 246, 0.04) 50%,
    rgba(236, 72, 153, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.detail-item:hover::before {
  opacity: 1;
}

.detail-item:hover {
  transform: translateX(5px);
  border-left-color: #8b5cf6;
}

.detail-item.description-item {
  background: rgba(66, 185, 131, 0.04);
  border-left-color: #42b983;
}

.detail-icon {
  color: #3a8ee6;
  margin-top: 2px;
  flex-shrink: 0;
}

.description-item .detail-icon {
  color: #42b983;
}

.detail-content {
  flex: 1;
}

.detail-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  display: block;
}

.detail-value {
  font-size: 0.95rem;
  color: #334155;
  line-height: 1.5;
}

.description-item .detail-value {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-actions {
  display: flex;
  justify-content: flex-end;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #3a8ee6, #42b983);
  color: white;
  padding: 12px 24px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(58, 142, 230, 0.2);
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(58, 142, 230, 0.3);
}

.action-button svg {
  transition: transform 0.3s ease;
}

.action-button:hover svg {
  transform: translateX(4px);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .header-content {
    padding: 0 15px;
  }

  .title-section h1 {
    font-size: 2.8rem;
  }

  .filter-container {
    padding: 30px 15px;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 25px;
  }
}

@media (max-width: 768px) {
  .page-header {
    margin-top: 60px;
  }

  .header-background {
    padding: 60px 0 40px;
  }

  .title-section h1 {
    font-size: 2.2rem;
  }

  .title-section p {
    font-size: 1.1rem;
  }

  .filter-container {
    padding: 25px 15px;
  }

  .category-tags {
    justify-content: center;
  }

  .category-tag {
    padding: 10px 20px;
    font-size: 0.9rem;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .product-content {
    padding: 30px 15px;
  }

  .detail-item {
    flex-direction: column;
    gap: 8px;
  }

  .detail-icon {
    align-self: flex-start;
  }
}

/* 加载和错误状态样式 */
.loading-container,
.error-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  margin: 50px 0;
}

.loading-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.loading-icon {
  color: #3a8ee6;
  animation: rotate 2s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes productCenterGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  25% {
    background-position: 100% 50%;
  }
  50% {
    background-position: 100% 100%;
  }
  75% {
    background-position: 0% 100%;
  }
}

@keyframes headerGradient {
  0%, 100% {
    background-position: 0% 50%;
  }
  33% {
    background-position: 100% 50%;
  }
  66% {
    background-position: 50% 100%;
  }
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

@keyframes textShine {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes gradientText {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes productCardFloat {
  0%, 100% {
    transform: translateY(0px) rotateX(0deg) rotateY(0deg);
  }
  25% {
    transform: translateY(-3px) rotateX(1deg) rotateY(0.5deg);
  }
  50% {
    transform: translateY(0px) rotateX(0deg) rotateY(-0.5deg);
  }
  75% {
    transform: translateY(2px) rotateX(-0.5deg) rotateY(0.5deg);
  }
}

.retry-btn {
  margin-top: 20px;
  background-color: #3a8ee6;
  color: white;
  border: none;
  padding: 10px 25px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
}

.retry-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-3px);
}

.pagination-container {
  margin-top: 50px;
  display: flex;
  justify-content: center;
}

/* 现代化响应式设计 */
@media (max-width: 1200px) {
  .products-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
  }

  .header-background {
    padding: 100px 0 60px;
  }

  .title-section h1 {
    font-size: 3.5rem;
  }
}

@media (max-width: 992px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .filter-container {
    padding: 40px 20px;
  }

  .category-tags {
    gap: 12px;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .title-section h1 {
    font-size: 3rem;
  }

  .title-section p {
    font-size: 1.4rem;
  }
}

@media (max-width: 768px) {
  .products-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .header-background {
    padding: 80px 0 50px;
  }

  .title-section h1 {
    font-size: 2.5rem;
  }

  .title-section p {
    font-size: 1.2rem;
  }

  .category-tags {
    justify-content: center;
  }

  .category-tag {
    padding: 12px 20px;
  }

  .product-content {
    padding: 25px;
  }

  .product-image-container {
    height: 200px;
  }
}

/* 小型加载指示器 */
.small-loading {
  display: flex;
  align-items: center;
  margin-top: 10px;
  font-size: 0.85rem;
  color: #3a8ee6;
}

.loading-icon-sm {
  font-size: 16px;
  margin-right: 5px;
  animation: rotate 2s linear infinite;
}

/* 全息投影系统核心动画 */
@keyframes quantumSpin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes reconstructionOrbit {
  from { transform: rotate(0deg) translateX(40px) rotate(0deg); }
  to { transform: rotate(360deg) translateX(40px) rotate(-360deg); }
}

@keyframes textGlow {
  0%, 100% { text-shadow: 0 0 15px rgba(0, 212, 255, 0.5); }
  50% { text-shadow: 0 0 25px rgba(0, 212, 255, 0.8); }
}

@keyframes tunnelPulse {
  0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
}

@keyframes quantumFloat {
  0%, 100% { transform: translateY(0px); opacity: 0.6; }
  50% { transform: translateY(-10px); opacity: 1; }
}

@keyframes streamFlow {
  0% { stroke-dashoffset: 20; opacity: 0; }
  50% { opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}

@keyframes scannerMove {
  0%, 100% { transform: translateX(-100%); opacity: 0; }
  50% { transform: translateX(0%); opacity: 1; }
}

@keyframes charFloat {
  0%, 100% { transform: translateY(0px) rotateX(0deg); }
  50% { transform: translateY(-5px) rotateX(5deg); }
}

@keyframes borderScan {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

@keyframes magneticPulse {
  0%, 100% { opacity: 0; transform: scale(0.8); }
  50% { opacity: 0.6; transform: scale(1.2); }
}

@keyframes magneticFieldExpand {
  0% { opacity: 1; transform: scale(0.5); }
  100% { opacity: 0; transform: scale(2); }
}

@keyframes particleFloat {
  0%, 100% { transform: translateY(0px); opacity: 0.4; }
  50% { transform: translateY(-8px); opacity: 1; }
}

@keyframes gridLineVertical {
  0%, 100% { opacity: 0; height: 0%; }
  50% { opacity: 1; height: 100%; }
}

@keyframes gridLineHorizontal {
  0%, 100% { opacity: 0; width: 0%; }
  50% { opacity: 1; width: 100%; }
}

/* 重力悬浮产品矩阵 */
.gravity-product-matrix {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 40px;
  position: relative;
}

.floating-product-cell {
  position: relative;
  background: rgba(0, 212, 255, 0.05);
  backdrop-filter: blur(20px);
  border-radius: 25px;
  border: 2px solid rgba(0, 212, 255, 0.2);
  padding: 25px;
  cursor: pointer;
  transform-style: preserve-3d;
  transition: all 0.6s ease;
  animation: productFloat 6s ease-in-out infinite;
  overflow: hidden;
}

.floating-product-cell:hover,
.floating-product-cell.field-activated {
  transform: translateY(-15px) rotateX(5deg) rotateY(5deg);
  border-color: rgba(0, 212, 255, 0.6);
  box-shadow: 0 0 40px rgba(0, 212, 255, 0.3);
}

/* 产品能量场 */
.product-energy-field {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.floating-product-cell:hover .product-energy-field,
.floating-product-cell.field-activated .product-energy-field {
  opacity: 1;
}

.field-rings {
  position: relative;
  width: 100%;
  height: 100%;
}

.energy-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50%;
  animation: energyPulse 2s ease-in-out infinite;
}

.energy-ring:nth-child(1) {
  width: 60%;
  height: 60%;
  margin: -30% 0 0 -30%;
}

.energy-ring:nth-child(2) {
  width: 80%;
  height: 80%;
  margin: -40% 0 0 -40%;
  animation-delay: 0.3s;
}

.energy-ring:nth-child(3) {
  width: 100%;
  height: 100%;
  margin: -50% 0 0 -50%;
  animation-delay: 0.6s;
}

/* 全息图像投影 */
.holographic-image {
  position: relative;
  margin-bottom: 20px;
  border-radius: 15px;
  overflow: hidden;
  height: 200px;
}

.image-projection {
  position: relative;
  width: 100%;
  height: 100%;
}

.product-holo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.9) contrast(1.1);
  transition: all 0.6s ease;
}

.floating-product-cell:hover .product-holo-image {
  filter: brightness(1.1) contrast(1.2) saturate(1.3);
  transform: scale(1.1);
}

.projection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 212, 255, 0.1);
  opacity: 0;
  transition: opacity 0.6s ease;
}

.floating-product-cell:hover .projection-overlay {
  opacity: 1;
}

.scan-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
  animation: scanLine 2s linear infinite;
}

.scan-line:nth-child(1) { top: 20%; animation-delay: 0s; }
.scan-line:nth-child(2) { top: 40%; animation-delay: 0.4s; }
.scan-line:nth-child(3) { top: 60%; animation-delay: 0.8s; }
.scan-line:nth-child(4) { top: 80%; animation-delay: 1.2s; }
.scan-line:nth-child(5) { top: 100%; animation-delay: 1.6s; }

.hologram-distortion {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0, 212, 255, 0.1) 2px,
    rgba(0, 212, 255, 0.1) 4px
  );
  animation: distortionFlicker 3s ease-in-out infinite;
}

/* 分类标识投影 */
.category-projection {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 212, 255, 0.8);
  border-radius: 15px;
  padding: 8px 15px;
  backdrop-filter: blur(10px);
}

.category-core {
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
}

.category-glow {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, rgba(0, 212, 255, 0.6), rgba(139, 92, 246, 0.6));
  border-radius: 17px;
  z-index: -1;
  animation: categoryGlow 2s ease-in-out infinite;
}

/* 已购买标签 */
.purchased-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
  backdrop-filter: blur(10px);
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  color: white;
  z-index: 11;
  box-shadow: 0 8px 25px rgba(82, 196, 26, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  gap: 4px;
  animation: purchasedPulse 2s ease-in-out infinite;
}

@keyframes purchasedPulse {
  0%, 100% {
    box-shadow: 0 8px 25px rgba(82, 196, 26, 0.4);
  }
  50% {
    box-shadow: 0 8px 35px rgba(82, 196, 26, 0.6);
  }
}

/* 信息数据流投影 */
.data-stream-projection {
  position: relative;
}

.product-name-hologram {
  position: relative;
  margin-bottom: 15px;
  text-align: center;
}

.holo-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  position: relative;
}

.name-scanner {
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  animation: scannerMove 3s ease-in-out infinite;
}

.product-data-matrix {
  margin-bottom: 20px;
}

.data-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  position: relative;
}

.data-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

.data-value {
  font-size: 0.9rem;
  color: rgba(0, 212, 255, 0.9);
  font-weight: 500;
  max-width: 60%;
  text-align: right;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.price-highlight {
  color: #FF6B6B;
  font-size: 1.3rem;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(255, 107, 107, 0.5);
}

.data-flow-line {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.6), transparent);
  animation: dataFlow 2s ease-in-out infinite;
}

/* 量子访问按钮 */
.quantum-access {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.access-btn {
  background: linear-gradient(45deg, rgba(0, 212, 255, 0.8), rgba(139, 92, 246, 0.8));
  border: 2px solid rgba(0, 212, 255, 0.4);
  border-radius: 20px;
  padding: 12px 24px;
  color: white;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
  pointer-events: auto;
  z-index: 20;
}

.access-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 0 25px rgba(0, 212, 255, 0.6);
}

.btn-energy-trail {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.access-btn:hover .btn-energy-trail {
  transform: translateX(100%);
}

/* 产品重力扭曲效果 */
.gravity-distortion {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 150%;
  height: 150%;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.6s ease;
}

.floating-product-cell:hover .gravity-distortion {
  opacity: 1;
}

.distortion-field {
  position: absolute;
  top: 50%;
  left: 50%;
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 50%;
  animation: distortionPulse 3s ease-in-out infinite;
}

.distortion-field:nth-child(1) {
  width: 80%;
  height: 80%;
  margin: -40% 0 0 -40%;
}

.distortion-field:nth-child(2) {
  width: 100%;
  height: 100%;
  margin: -50% 0 0 -50%;
  animation-delay: 1s;
}

/* 更多动画关键帧 */
@keyframes productFloat {
  0%, 100% { transform: translateY(0px) rotateX(0deg) rotateY(0deg); }
  33% { transform: translateY(var(--float-amplitude, 15px)) rotateX(2deg) rotateY(1deg); }
  66% { transform: translateY(calc(var(--float-amplitude, 15px) * -0.5)) rotateX(-1deg) rotateY(-1deg); }
}

@keyframes energyPulse {
  0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.05); }
}

@keyframes scanLine {
  0%, 100% { opacity: 0; transform: translateY(-10px); }
  50% { opacity: 1; transform: translateY(0px); }
}

@keyframes distortionFlicker {
  0%, 100% { opacity: 0.1; }
  50% { opacity: 0.3; }
}

@keyframes categoryGlow {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@keyframes dataFlow {
  0% { width: 0%; opacity: 0; }
  50% { opacity: 1; }
  100% { width: 100%; opacity: 0; }
}

@keyframes distortionPulse {
  0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1); }
  50% { opacity: 0.6; transform: translate(-50%, -50%) scale(1.1); }
}

/* ===== 支付弹窗样式 ===== */
.payment-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

.payment-dialog {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 450px;
  width: 90%;
  overflow: hidden;
  animation: slideUp 0.4s ease;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.payment-header {
  background: linear-gradient(135deg, #007AFF 0%, #0051D5 100%);
  color: white;
  padding: 24px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.payment-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: rotate 10s linear infinite;
}

.payment-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  z-index: 1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 1;
  color: white;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.payment-content {
  padding: 35px 30px;
}

.product-info {
  text-align: center;
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 2px dashed rgba(0, 122, 255, 0.2);
}

.product-info h4 {
  margin: 0 0 12px 0;
  font-size: 1.3rem;
  color: #333;
  font-weight: 600;
}

.price {
  font-size: 2.2rem;
  color: #007AFF;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
}

.qr-code-container {
  text-align: center;
}

.qr-code {
  width: 220px;
  height: 220px;
  margin: 0 auto 20px;
  padding: 15px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  display: block;
  border: 3px solid #007AFF;
  transition: all 0.3s;
}

.qr-code:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px rgba(0, 122, 255, 0.3);
}

.qr-tip {
  color: #666;
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 576px) {
  .payment-dialog {
    width: 95%;
    max-width: none;
  }
  
  .payment-header {
    padding: 20px 20px;
  }
  
  .payment-header h3 {
    font-size: 1.3rem;
  }
  
  .payment-content {
    padding: 25px 20px;
  }
  
  .product-info h4 {
    font-size: 1.1rem;
  }
  
  .price {
    font-size: 1.8rem;
  }
  
  .qr-code {
    width: 180px;
    height: 180px;
  }
}
</style> 